import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ear, PenTool, Lightbulb } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../data/translations';

const CommitmentsSection = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [activeIdx, setActiveIdx] = useState(0);

  const items = [
    { key: 'listen', label: getTranslation(language, 'commitmentListen'), Icon: Ear, quote: getTranslation(language, 'commitmentQuote1') },
    { key: 'propose', label: getTranslation(language, 'commitmentPropose'), Icon: PenTool, quote: getTranslation(language, 'commitmentQuote2') },
    { key: 'innovate', label: getTranslation(language, 'commitmentInnovate'), Icon: Lightbulb, quote: getTranslation(language, 'commitmentQuote3') },
  ];

  useEffect(() => {
    const id = setInterval(() => setActiveIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  const active = items[activeIdx];

  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden"
      style={{ backgroundColor: '#702963' }}
      data-testid="commitments-section"
    >
      {/* Faint blueprint texture */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-14">
          <h2 className="text-white text-3xl md:text-5xl font-bold tracking-wide" data-testid="commitments-title">
            {getTranslation(language, 'commitmentsTitle')}
          </h2>
          <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-400 rounded-full" />
        </div>

        {/* Three circular icons */}
        <div className="grid grid-cols-3 gap-6 md:gap-16 max-w-3xl mx-auto">
          {items.map((it, i) => {
            const Icon = it.Icon;
            const isActive = i === activeIdx;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => setActiveIdx(i)}
                className="group flex flex-col items-center focus:outline-none"
                data-testid={`commitment-${it.key}`}
              >
                <div
                  className={`w-20 h-20 md:w-28 md:h-28 rounded-full flex items-center justify-center transition-all duration-500 ${
                    isActive
                      ? 'bg-white text-[#702963] scale-110 shadow-2xl'
                      : 'bg-white/15 text-white ring-2 ring-white/60 group-hover:bg-white/25'
                  }`}
                >
                  <Icon className="w-9 h-9 md:w-12 md:h-12" strokeWidth={2} />
                </div>
                <span
                  className={`mt-4 text-base md:text-xl font-semibold transition-colors ${
                    isActive ? 'text-white' : 'text-white/80 group-hover:text-white'
                  }`}
                >
                  {it.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Quote box with brackets */}
        <div className="mt-14 md:mt-16 max-w-3xl mx-auto">
          <div className="relative px-8 md:px-14 py-10 md:py-12">
            {/* Corner brackets */}
            <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-white/80" />
            <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white/80" />
            <span className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-white/80" />
            <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-white/80" />

            <p
              key={activeIdx}
              className="text-white text-center italic text-base md:text-lg leading-relaxed transition-opacity duration-500"
              data-testid="commitment-quote"
            >
              {active.quote}
            </p>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`h-2 rounded-full transition-all ${
                  i === activeIdx ? 'w-8 bg-orange-400' : 'w-2 bg-white/40 hover:bg-white/70'
                }`}
                aria-label={`quote ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <button
            type="button"
            onClick={() => navigate('/qui-sommes-nous')}
            className="inline-flex items-center gap-2 border-2 border-white text-white font-semibold tracking-wide px-8 py-3 rounded-none hover:bg-white hover:text-[#702963] transition-all duration-300"
            data-testid="commitments-learn-more-btn"
          >
            {getTranslation(language, 'commitmentsLearnMore')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default CommitmentsSection;
